import state from '@/taskpane/state/sidebar-state'
import AttachmentInfo from '@/taskpane/events/models/attachment-info'
import { officeIsComposeMode } from './office-mail-mode'
import AttachmentContents from '@/taskpane/events/models/attachment-contents'
import {
  officeApiItem_AttachmentsGetAsync,
  officeApiItem_AttachmentsGetContentsAsync,
  officeApiItem_GetAsFileAsync,
} from './async/office-api-mailbox-item-async'
import { officeMailDateTimeSent } from './office-message'
import officeApiWrapper from '../common/office-api-wrapper'

function canGetAttachments(): boolean {
  return !!Office.context.mailbox.item.getAttachmentContentAsync || state.currentItem.apiInfo.isEwsApiAvailable || officeApiWrapper.isAvailable()
}

export async function officeAttachments(): Promise<AttachmentInfo[]> {
  let attachments: Office.AttachmentDetails[] | Office.AttachmentDetailsCompose[]
  const filteredAttachments: AttachmentInfo[] = []

  if (!canGetAttachments()) {
    attachments = []
  } else if (officeIsComposeMode()) {
    attachments = await officeApiItem_AttachmentsGetAsync()
  } else {
    attachments = Office.context.mailbox.item.attachments || []
  }

  attachments.forEach((item: Office.AttachmentDetails | Office.AttachmentDetailsCompose) => {
    if (item.name.indexOf('Device Independent Bitmap') >= 0) {
      return
    }

    filteredAttachments.push({
      name: item.name,
      id: item.id,
      type: (item as Office.AttachmentDetails).contentType,
      size: item.size,
      inline: item.isInline,
      url: null,
    } as AttachmentInfo)
  })

  return filteredAttachments
}

export async function officeAttachmentContents(selectedAttachment: AttachmentInfo): Promise<AttachmentContents> {
  const attachmentResult = await officeApiItem_AttachmentsGetContentsAsync(selectedAttachment.id)

  if (attachmentResult.content && attachmentResult.format == Office.MailboxEnums.AttachmentContentFormat.Base64) {
    return {
      ...selectedAttachment,
      contentsBase64: attachmentResult.content,
    } as AttachmentContents
  } else {
    throw 'Unable to download attachment'
  }
}

export async function officeEmailAsEmlAttachment(): Promise<AttachmentContents> {
  if (officeIsComposeMode()) {
    throw 'Unable to save an email that is being composed'
  }

  const contentsBase64 = await officeApiItem_GetAsFileAsync()
  if (!contentsBase64) {
    throw 'Unable to save email as a file'
  }

  return {
    id: 'email-eml',
    emailId: Office.context.mailbox.item.itemId || '',
    name: emlFileName(Office.context.mailbox.item.subject || '', officeMailDateTimeSent()),
    size: base64DecodedSize(contentsBase64),
    type: 'message/rfc822',
    inline: false,
    url: null,
    selected: true,
    contentsBase64,
    // placeholders required by AttachmentContents; overwritten by uploadAttachments
    totalChunks: 1,
    chunkId: 'email-eml',
  } as AttachmentContents
}

function base64DecodedSize(base64: string): number {
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
  return Math.floor((base64.length * 3) / 4) - padding
}

function emlFileName(subject: string, date: Date | null): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  const d = date ?? new Date()
  const stamp = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}${pad(d.getMinutes())}`
  const safeSubject =
    subject
      .replace(/[\\/:*?"<>|]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .substring(0, 150) || 'Email'
  return `${safeSubject} ${stamp}.eml`
}

const exportFunctions = {
  get: officeAttachments,
  getContents: officeAttachmentContents,
  getEmailAsEml: officeEmailAsEmlAttachment,
}

export default exportFunctions
